/**
 * API service for Creative Studio operations.
 *
 * Provides functions for:
 * - Image generation from text prompts
 * - Multi-turn editing sessions
 * - Saving generated images to Media Library
 */
import { apiClient } from './client';
import type {
  AspectRatio,
  ImageResolution,
  GenerateImageRequest,
  GenerationJobResponse,
  GenerationResultResponse,
  StartRefinementSessionRequest,
  ApplyRefinementRequest,
  AddSessionReferenceImageRequest,
  RenameSessionReferenceImageRequest,
  RefinementSessionResponse,
  SaveToMediaLibraryRequest,
  SaveToMediaLibraryResponse,
  AdFormatPreset,
  MarkShade,
} from '@/types/creativeStudio';

// =============================================================================
// Image Generation
// =============================================================================

/**
 * Start an async image generation job.
 *
 * Returns immediately with a job_id for polling.
 * Use pollGenerationResult() to check for completion.
 */
export async function startGeneration(
  params: GenerateImageRequest
): Promise<GenerationJobResponse> {
  const response = await apiClient.post<GenerationJobResponse>(
    '/creative-studio/generate',
    params,
    { timeout: 60000 } // 1 minute timeout for initial request
  );
  return response.data;
}

/**
 * Poll for generation job results.
 *
 * Call this every 2-3 seconds until status is 'completed' or 'failed'.
 */
export async function pollGenerationResult(jobId: string): Promise<GenerationResultResponse> {
  const response = await apiClient.get<GenerationResultResponse>(
    `/creative-studio/generate/${jobId}`
  );
  return response.data;
}

/**
 * End a refinement session.
 *
 * Note: Unsaved images will be cleaned up after session expiry.
 */
export async function endSession(sessionId: string): Promise<void> {
  await apiClient.delete(`/creative-studio/sessions/${sessionId}`);
}

// =============================================================================
// Refinement Sessions
// =============================================================================

/**
 * Start a multi-turn refinement session after initial generation.
 *
 * This is the recommended flow for iterative image improvement:
 * 1. Generate an image with a prompt (optionally with a reference image)
 * 2. If not satisfied, call this to start refining
 * 3. Use applyRefinement() to apply critiques
 * 4. Repeat until satisfied, then save to Media Library
 */
export async function startRefinementSession(
  params: StartRefinementSessionRequest
): Promise<RefinementSessionResponse> {
  const response = await apiClient.post<RefinementSessionResponse>(
    '/creative-studio/sessions/refine',
    params,
    { timeout: 60000 }
  );
  return response.data;
}

/**
 * Apply a refinement critique to the current image.
 *
 * The model receives:
 * - All named reference images attached to the session, in order
 * - Current generated image (last)
 * - Original prompt context
 * - Your critique/refinement instruction
 *
 * The critique may refer to any attached image by its name.
 *
 * Examples of critique:
 * - "The text is too small, make it larger and more prominent"
 * - "The colors are too muted, make them more vibrant"
 * - "Add a subtle drop shadow behind the product"
 */
export async function applyRefinement(
  sessionId: string,
  params: ApplyRefinementRequest
): Promise<RefinementSessionResponse> {
  const response = await apiClient.post<RefinementSessionResponse>(
    `/creative-studio/sessions/${sessionId}/refine`,
    params,
    { timeout: 120000 } // 2 minutes for refinement
  );
  return response.data;
}

/**
 * Get the current state of a refinement session with signed URLs.
 */
export async function getRefinementSession(
  sessionId: string
): Promise<RefinementSessionResponse> {
  const response = await apiClient.get<RefinementSessionResponse>(
    `/creative-studio/sessions/${sessionId}/status`
  );
  return response.data;
}

/**
 * Revert a refinement session to a previous state.
 *
 * This allows users to undo refinements that didn't work out and try again.
 * The session history is truncated to the target index.
 *
 * @param sessionId - The refinement session ID
 * @param targetIndex - The history index to revert to (0 = original, 1 = first refinement, etc.)
 */
export async function revertRefinement(
  sessionId: string,
  targetIndex: number
): Promise<RefinementSessionResponse> {
  const response = await apiClient.post<RefinementSessionResponse>(
    `/creative-studio/sessions/${sessionId}/revert/${targetIndex}`
  );
  return response.data;
}

// =============================================================================
// Session Reference Image Management
// =============================================================================

/**
 * Attach an additional reference image to an active refinement session.
 *
 * The new image will be passed to Gemini on subsequent refinements, allowing
 * the user to reference it by name (e.g., "place the logo top-left") inside
 * their critique.
 */
export async function addSessionReferenceImage(
  sessionId: string,
  params: AddSessionReferenceImageRequest
): Promise<RefinementSessionResponse> {
  const response = await apiClient.post<RefinementSessionResponse>(
    `/creative-studio/sessions/${sessionId}/reference-images`,
    params
  );
  return response.data;
}

/**
 * Remove a reference image from an active refinement session.
 */
export async function removeSessionReferenceImage(
  sessionId: string,
  mediaImageId: string
): Promise<RefinementSessionResponse> {
  const response = await apiClient.delete<RefinementSessionResponse>(
    `/creative-studio/sessions/${sessionId}/reference-images/${mediaImageId}`
  );
  return response.data;
}

/**
 * Rename a reference image attached to an active refinement session.
 *
 * The React UI no longer surfaces a rename affordance (names are
 * auto-assigned as `ref-image-N`). The function is retained for
 * compatibility with any non-FE caller; the backend PATCH endpoint
 * also remains.
 */
export async function renameSessionReferenceImage(
  sessionId: string,
  mediaImageId: string,
  params: RenameSessionReferenceImageRequest
): Promise<RefinementSessionResponse> {
  const response = await apiClient.patch<RefinementSessionResponse>(
    `/creative-studio/sessions/${sessionId}/reference-images/${mediaImageId}`,
    params
  );
  return response.data;
}

/**
 * Upload a session-only reference mark (annotation overlay) to an active
 * refinement session.
 *
 * `blob` should be a PNG of ONLY the transparent stroke overlay the user
 * drew — no base image pixels. The backend composites it onto the
 * session's current image at native resolution and stores the composite
 * under a session-scoped GCS path; it is NOT added to the Media Library.
 * The FE-only overlay payload is what lets us avoid drawing the
 * cross-origin <img> into a canvas, which would taint it under typical
 * GCS-without-CORS deployments. `markShade` records which marker color
 * the user picked so the backend can tell Gemini which stroke to look for.
 */
export async function addSessionReferenceMark(
  sessionId: string,
  blob: Blob,
  markShade: MarkShade,
  bbox: {
    x_frac: number;
    y_frac: number;
    w_frac: number;
    h_frac: number;
  } | null = null
): Promise<RefinementSessionResponse> {
  const formData = new FormData();
  formData.append('image', new File([blob], 'reference-mark.png', { type: 'image/png' }));
  formData.append('mark_shade', markShade);
  if (bbox) {
    // Send normalized fractions; the backend derives qualitative
    // position/coverage hints from these and weaves them into the
    // refinement prompt so Gemini doesn't have to find the stroke
    // purely by color in the image.
    formData.append('bbox_x_frac', String(bbox.x_frac));
    formData.append('bbox_y_frac', String(bbox.y_frac));
    formData.append('bbox_w_frac', String(bbox.w_frac));
    formData.append('bbox_h_frac', String(bbox.h_frac));
  }
  const response = await apiClient.post<RefinementSessionResponse>(
    `/creative-studio/sessions/${sessionId}/reference-marks`,
    formData,
    {
      // Match the rest of the codebase (see media.ts). The apiClient has
      // a default Content-Type: application/json — omitting the header
      // here would let that default leak through and the server returns
      // 422 because it can't parse the multipart body. Axios v1's XHR
      // adapter clears this for FormData bodies in the browser and lets
      // the browser add the boundary, so the explicit value is safe.
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }
  );
  return response.data;
}

// =============================================================================
// Save to Media Library
// =============================================================================

/**
 * Save a generated or edited image to the Media Library.
 *
 * Provide either session_id (for edited images) or
 * generation_job_id (for newly generated images).
 */
export async function saveToMediaLibrary(
  params: SaveToMediaLibraryRequest
): Promise<SaveToMediaLibraryResponse> {
  const response = await apiClient.post<SaveToMediaLibraryResponse>(
    '/creative-studio/save',
    params
  );
  return response.data;
}

// =============================================================================
// Presets & Configuration
// =============================================================================

/**
 * Get available ad format presets.
 */
export async function getPresets(): Promise<AdFormatPreset[]> {
  const response = await apiClient.get<AdFormatPreset[]>('/creative-studio/presets');
  return response.data;
}

/**
 * Get available aspect ratios.
 */
export async function getAspectRatios(): Promise<{ value: AspectRatio; name: string }[]> {
  const response = await apiClient.get<{ value: AspectRatio; name: string }[]>(
    '/creative-studio/aspect-ratios'
  );
  return response.data;
}

/**
 * Get available resolutions.
 */
export async function getResolutions(): Promise<{ value: ImageResolution; name: string }[]> {
  const response = await apiClient.get<{ value: ImageResolution; name: string }[]>(
    '/creative-studio/resolutions'
  );
  return response.data;
}
